import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'role-interface-definition' | 'structural-interface-snapshot' | 'context-localization' | 'full-diagnostic';
export function WorkRelationshipDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [hasBaseline] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'role-interface-definition' : 'baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  // Baseline data
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Role interface definition (replaces partner baseline)
  const [roleInterface, setRoleInterface] = useState({
    relationshipType: '',
    interactionFrequency: '',
    powerImpact: ''
  });
  // Context localization
  const [contextData, setContextData] = useState({
    frictionZones: [] as string[],
    internalState: '',
    contextNote: ''
  });
  // Structural interface snapshot
  const structuralSnapshot = {
    userWorkPattern: 'You operate as a system stabilizer at work. You naturally move toward building structure, organizing complexity, and creating clarity before executing. You prefer autonomy over constant guidance, depth over speed, and behind-the-scenes architecture over high-visibility performance.',
    roleActivation: 'Around this manager role, your system shifts into heightened vigilance. You become more contained, more precise, and less spontaneous. The autonomy you naturally operate from tightens. You start tracking their expectations more closely than your own judgment. Your internal processing slows because you are running two systems simultaneously: your own and your model of theirs.',
    compensationStrategy: 'Your system compensates by over-functioning cognitively. You prepare more than necessary, anticipate questions before they are asked, and build structure to prevent ambiguity. This creates the appearance of high performance but costs internal coherence. You are managing their perception more than executing from your center.',
    invisibleCost: 'This interaction quietly costs you speed and confidence. Decisions that would normally be clear require more internal processing. Your natural pace slows. The energy spent managing the interface reduces the energy available for creative or strategic work. Over time, this creates a subtle but persistent drain.'
  };
  // Full diagnostic output
  const fullDiagnosticOutput = {
    patternRoleInterface: 'This dynamic forms because your system is wired for autonomy and internal structure, while this manager role introduces external evaluation and authority. Your pattern interprets authority as potential constraint, which triggers preemptive structure-building to maintain control. This is not about their management style — it is about how your system processes authority.',
    repeatingLoop: 'The loop: Authority presence → vigilance increase → over-preparation → temporary relief → pattern reinforcement. Each cycle makes the pattern more automatic. The over-functioning feels like competence, which rewards the pattern, which makes it harder to notice the cost.',
    authorityDistortion: 'Power distorts this interaction by making your system prioritize their perception over your own judgment. When authority is above you, your internal compass recalibrates to their expectations. This is not conscious compliance — it is structural adaptation. The cost is that your own clarity becomes secondary.',
    growthBreakdownBoundary: 'This dynamic caps you where autonomy and authority intersect. Roles that require high visibility under authority will trigger this pattern most intensely. Growth requires holding your own center under external evaluation — not managing perception. Breakdown occurs when the compensation strategy exhausts the system faster than it can recover.',
    whatChanged: {
      baseline: 'The baseline showed your structural work pattern: system stabilizer, autonomy preference, depth over speed, behind-the-scenes orientation.',
      roleInterface: 'Adding the role interface (manager, daily interaction, direct power impact) revealed how your pattern shifts: heightened vigilance, over-functioning, perception management, and the invisible cost to speed and confidence.',
      whyNotPersonalities: 'This is not about personalities. It is about how your system structurally responds to authority interfaces. The same pattern would activate with any manager role that introduces external evaluation and constraint.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    setIsGenerating(true);
    setGeneratingMessage('Saving your baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('role-interface-definition');
    }, 1500);
  };
  const handleRoleInterfaceSubmit = () => {
    if (!roleInterface.relationshipType || !roleInterface.interactionFrequency || !roleInterface.powerImpact) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating interface snapshot...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('structural-interface-snapshot');
    }, 2000);
  };
  const handleContextSubmit = () => {
    setIsGenerating(true);
    setGeneratingMessage('Integrating context...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('full-diagnostic');
    }, 2000);
  };
  const handleToggleFrictionZone = (zone: string) => {
    setContextData(prev => ({
      ...prev,
      frictionZones: prev.frictionZones.includes(zone) ? prev.frictionZones.filter(z => z !== zone) : [...prev.frictionZones, zone]
    }));
  };
  if (isGenerating) {
    return <>
        <Helmet data-id="element-5153">
          <title data-id="element-5154">
            Generating Work Relationship Diagnostic | Lazy Monk Labs
          </title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-5155">
          <div className="text-center" data-id="element-5156">
            <div className="mb-6" data-id="element-5157">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-5158"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-5159">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-5160">
        <title data-id="element-5161">Work Relationship Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-5162">
        <header data-id="element-5163">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-5164">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-5165">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-5166">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-5167" />
                <span className="text-lg font-medium tracking-tight" data-id="element-5168">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-5169">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-5170">
          <div className="max-w-2xl mx-auto" data-id="element-5171">
            {/* PHASE 1: Baseline Collection - Abbreviated */}
            {currentPhase === 'baseline-collection' && <div data-id="element-5172">
                <div className="mb-12" data-id="element-5173">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-5174">
                    Work Relationship Decode
                  </h1>
                  <p className="text-lg text-[#F5F5F5]/60 italic mb-4" data-id="element-5175">
                    A diagnostic of how your system behaves around a specific
                    work role
                  </p>
                  <p className="text-base text-[#F5F5F5]/70 leading-relaxed" data-id="element-5176">
                    This report maps how your internal pattern adapts, tightens,
                    or compensates when interfacing with a specific work
                    authority or role — without judging either side.
                  </p>
                </div>
                <div className="space-y-8 mb-12" data-id="element-5177">
                  <div data-id="element-5178">
                    <label className="block text-sm font-medium mb-3" data-id="element-5179">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-5180">
                        <div className="text-2xl font-medium" data-id="element-5181">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-5182">
                      <div data-id="element-5183">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-5184">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-5185">
                          <option value="" data-id="element-5186">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-5187">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-5188">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-5189">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-5190">
                          <option value="" data-id="element-5191">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-5192">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-5193">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-5194">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-5195">
                          <option value="" data-id="element-5196">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-5197">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-5198">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-5199">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-5200" />
                  </div>
                  <div data-id="element-5201">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-5202">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-5203" />
                  </div>
                </div>
                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-5204">
                  Continue
                </button>
              </div>}

            {/* PHASE 2: Role Interface Definition (CRITICAL - replaces partner baseline) */}
            {currentPhase === 'role-interface-definition' && <div data-id="element-5205">
                <div className="mb-12" data-id="element-5206">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-5207">
                    Who are you decoding?
                  </h1>
                </div>
                <div className="space-y-12 mb-12" data-id="element-5208">
                  <div data-id="element-5209">
                    <h2 className="text-lg font-medium mb-6" data-id="element-5210">
                      This person is primarily:
                    </h2>
                    <div className="space-y-3" data-id="element-5211">
                      {['My manager / boss / skip-level / senior leader', 'A colleague / peer / co-founder', 'Someone who reports to me'].map(type => <label key={type} className={`block border p-5 cursor-pointer transition-colors ${roleInterface.relationshipType === type ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-5212">
                          <input type="radio" name="relationshipType" value={type} checked={roleInterface.relationshipType === type} onChange={e => setRoleInterface(prev => ({
                      ...prev,
                      relationshipType: e.target.value
                    }))} className="sr-only" data-id="element-5213" />
                          <span className="text-base" data-id="element-5214">{type}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-5215">
                    <h2 className="text-lg font-medium mb-6" data-id="element-5216">
                      Interaction frequency
                    </h2>
                    <div className="space-y-3" data-id="element-5217">
                      {['Daily', 'Weekly', 'Occasionally', 'Only in high-stakes situations'].map(freq => <label key={freq} className={`block border p-5 cursor-pointer transition-colors ${roleInterface.interactionFrequency === freq ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-5218">
                          <input type="radio" name="interactionFrequency" value={freq} checked={roleInterface.interactionFrequency === freq} onChange={e => setRoleInterface(prev => ({
                      ...prev,
                      interactionFrequency: e.target.value
                    }))} className="sr-only" data-id="element-5219" />
                          <span className="text-base" data-id="element-5220">{freq}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-5221">
                    <h2 className="text-lg font-medium mb-3" data-id="element-5222">
                      Their decisions affect your work:
                    </h2>
                    <div className="space-y-3" data-id="element-5223">
                      {['Directly', 'Indirectly', 'Occasionally', 'Barely'].map(impact => <label key={impact} className={`block border p-5 cursor-pointer transition-colors ${roleInterface.powerImpact === impact ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-5224">
                            <input type="radio" name="powerImpact" value={impact} checked={roleInterface.powerImpact === impact} onChange={e => setRoleInterface(prev => ({
                      ...prev,
                      powerImpact: e.target.value
                    }))} className="sr-only" data-id="element-5225" />
                            <span className="text-base" data-id="element-5226">{impact}</span>
                          </label>)}
                    </div>
                  </div>
                </div>
                <button onClick={handleRoleInterfaceSubmit} disabled={!roleInterface.relationshipType || !roleInterface.interactionFrequency || !roleInterface.powerImpact} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-5227">
                  Generate Snapshot
                </button>
              </div>}

            {/* PHASE 3: Structural Interface Snapshot (WOW MOMENT) */}
            {currentPhase === 'structural-interface-snapshot' && <div data-id="element-5228">
                <div className="mb-12" data-id="element-5229">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-5230">
                    Work Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-5231">
                    Interface snapshot
                  </p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5232">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5233">
                    <span className="text-2xl" data-id="element-5234">🧱</span>
                    <div className="flex-1" data-id="element-5235">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5236">
                        Your default pattern at work
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5237">
                        {structuralSnapshot.userWorkPattern}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5238">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5239">
                    <span className="text-2xl" data-id="element-5240">⚙️</span>
                    <div className="flex-1" data-id="element-5241">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5242">
                        What changes in you around this role
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5243">
                        {structuralSnapshot.roleActivation}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5244">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5245">
                    <span className="text-2xl" data-id="element-5246">🔁</span>
                    <div className="flex-1" data-id="element-5247">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5248">
                        How your system compensates
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5249">
                        {structuralSnapshot.compensationStrategy}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5250">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5251">
                    <span className="text-2xl" data-id="element-5252">⚠️</span>
                    <div className="flex-1" data-id="element-5253">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5254">
                        What this interaction quietly costs you
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5255">
                        {structuralSnapshot.invisibleCost}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 text-center" data-id="element-5256">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-5257">
                    This snapshot describes your pattern — not their intent or
                    psychology
                  </p>
                </div>
                <div className="mb-12" data-id="element-5258">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-5259">
                    <span data-id="element-5260">📘</span>
                    <span data-id="element-5261">How this snapshot is generated</span>
                    <span className="text-xs" data-id="element-5262">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-5263">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-5264">
                        This snapshot combines your structural work pattern with
                        the role interface you are operating under.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-5265">
                        It does not analyze the other person's psychology.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-5266">
                        It shows how your system adapts, compensates, or
                        constricts in this configuration.
                      </p>
                    </div>}
                </div>
                <div className="space-y-4" data-id="element-5267">
                  <button onClick={() => setCurrentPhase('context-localization')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-5268">
                    🧩 Add context (optional)
                  </button>
                  <div className="text-center" data-id="element-5269">
                    <p className="text-xs text-[#F5F5F5]/50 mb-2" data-id="element-5270">
                      Optional · 3 quick questions
                    </p>
                  </div>
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-5271">
                    Save Report
                  </button>
                </div>
              </div>}

            {/* PHASE 4: Context Localization */}
            {currentPhase === 'context-localization' && <div data-id="element-5272">
                <div className="mb-12" data-id="element-5273">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-5274">
                    Add context
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-5275">
                    Optional · 3 quick questions
                  </p>
                </div>
                <div className="space-y-12 mb-12" data-id="element-5276">
                  <div data-id="element-5277">
                    <h2 className="text-lg font-medium mb-3" data-id="element-5278">
                      Where does this relationship strain the most?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-5279">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-5280">
                      {['Authority / decision-making', 'Feedback / evaluation', 'Pace and urgency', 'Ownership ambiguity', 'Emotional tone'].map(zone => <label key={zone} className={`block border p-5 cursor-pointer transition-colors ${contextData.frictionZones.includes(zone) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-5281">
                          <input type="checkbox" value={zone} checked={contextData.frictionZones.includes(zone)} onChange={() => handleToggleFrictionZone(zone)} className="sr-only" data-id="element-5282" />
                          <span className="text-base" data-id="element-5283">{zone}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-5284">
                    <h2 className="text-lg font-medium mb-6" data-id="element-5285">
                      Around this person, you usually feel:
                    </h2>
                    <div className="space-y-3" data-id="element-5286">
                      {['Alert and contained', 'Tense or guarded', 'Over-responsible', 'Diminished or hesitant'].map(state => <label key={state} className={`block border p-5 cursor-pointer transition-colors ${contextData.internalState === state ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-5287">
                          <input type="radio" name="internalState" value={state} checked={contextData.internalState === state} onChange={e => setContextData(prev => ({
                      ...prev,
                      internalState: e.target.value
                    }))} className="sr-only" data-id="element-5288" />
                          <span className="text-base" data-id="element-5289">{state}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-5290">
                    <h2 className="text-lg font-medium mb-3" data-id="element-5291">
                      Anything important about this dynamic right now?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-5292">Optional</p>
                    <textarea value={contextData.contextNote} onChange={e => setContextData(prev => ({
                  ...prev,
                  contextNote: e.target.value
                }))} rows={3} maxLength={200} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional..." data-id="element-5293" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-5294">
                      {contextData.contextNote.length}/200
                    </p>
                  </div>
                </div>
                <button onClick={handleContextSubmit} disabled={!contextData.internalState} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-5295">
                  Apply Context
                </button>
              </div>}

            {/* PHASE 5: Full Diagnostic */}
            {currentPhase === 'full-diagnostic' && <div data-id="element-5296">
                <div className="mb-12" data-id="element-5297">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-5298">
                    Work Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-5299">Full Diagnostic</p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5300">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5301">
                    <span className="text-2xl" data-id="element-5302">🧠</span>
                    <div className="flex-1" data-id="element-5303">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5304">
                        Why this dynamic forms
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5305">
                        {fullDiagnosticOutput.patternRoleInterface}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5306">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5307">
                    <span className="text-2xl" data-id="element-5308">🔁</span>
                    <div className="flex-1" data-id="element-5309">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5310">
                        The loop that keeps repeating
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5311">
                        {fullDiagnosticOutput.repeatingLoop}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5312">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5313">
                    <span className="text-2xl" data-id="element-5314">🏗️</span>
                    <div className="flex-1" data-id="element-5315">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5316">
                        How power distorts this interaction
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5317">
                        {fullDiagnosticOutput.authorityDistortion}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5318">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5319">
                    <span className="text-2xl" data-id="element-5320">⚠️</span>
                    <div className="flex-1" data-id="element-5321">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5322">
                        Where this dynamic caps you
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5323">
                        {fullDiagnosticOutput.growthBreakdownBoundary}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12" data-id="element-5324">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-5325">
                    <span data-id="element-5326">🔍</span>
                    <span data-id="element-5327">What changed after adding context</span>
                    <span className="text-xs" data-id="element-5328">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-5329">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-5330">
                        <h3 className="text-sm font-medium mb-4" data-id="element-5331">
                          What baseline showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-5332">
                          {fullDiagnosticOutput.whatChanged.baseline}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-5333">
                        <h3 className="text-sm font-medium mb-4" data-id="element-5334">
                          What role interface revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-5335">
                          {fullDiagnosticOutput.whatChanged.roleInterface}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-5336">
                        <h3 className="text-sm font-medium mb-4" data-id="element-5337">
                          Why this is not about personalities
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-5338">
                          {fullDiagnosticOutput.whatChanged.whyNotPersonalities}
                        </p>
                      </div>
                    </div>}
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5339">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5340">
                    <span className="text-2xl" data-id="element-5341">🔁</span>
                    <div className="flex-1" data-id="element-5342">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5343">
                        Pattern Consistency Check
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-5344">
                        This reaction pattern appears with similar role
                        interfaces. The specific person changes, but the way
                        your system responds to this type of authority or power
                        dynamic remains structurally consistent. This is about
                        your system, not them.
                      </p>
                      <div className="space-y-4" data-id="element-5345">
                        <div data-id="element-5346">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-5347">
                            Other bosses / peers / reports
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-5348">
                            If you examine other relationships with similar role
                            structures, you will likely find the same internal
                            shifts. With other managers, the same vigilance and
                            over-functioning. With other peers, the same control
                            or withdrawal patterns. With other reports, the same
                            over-responsibility or distance. The people are
                            different, but your system's response to the role
                            interface is consistent.
                          </p>
                        </div>
                        <div data-id="element-5349">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-5350">
                            Similar power asymmetries
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-5351">
                            Across different organizations and contexts, the
                            same pattern activates when power dynamics are
                            similar. Authority above you triggers one response;
                            authority below you triggers another. The pattern is
                            not about the specific person's management style or
                            personality — it is about how your system
                            structurally processes power asymmetry.
                          </p>
                        </div>
                        <div data-id="element-5352">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-5353">
                            Repeated internal shifts
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-5354">
                            The same internal changes occur: heightened
                            vigilance, increased containment, perception
                            management, or withdrawal. These shifts are
                            automatic responses to the role interface, not
                            conscious choices. They repeat because they are
                            structural adaptations to authority configurations,
                            not reactions to individual personalities.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5355">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5356">
                    <span className="text-2xl" data-id="element-5357">⚖️</span>
                    <div className="flex-1" data-id="element-5358">
                      <h2 className="text-xl font-medium mb-6" data-id="element-5359">
                        Where This Diagnosis Applies — and Where It Doesn't
                      </h2>
                      <div className="space-y-6" data-id="element-5360">
                        <div data-id="element-5361">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-5362">
                            This diagnosis explains:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-5363">
                            <li data-id="element-5364">
                              • Why certain roles trigger specific internal
                              responses in you
                            </li>
                            <li data-id="element-5365">
                              • Why your behavior shifts around particular
                              authority configurations
                            </li>
                            <li data-id="element-5366">
                              • How your system adapts, compensates, or
                              constricts in this role interface
                            </li>
                            <li data-id="element-5367">
                              • What this dynamic quietly costs you over time
                            </li>
                          </ul>
                        </div>
                        <div data-id="element-5368">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-5369">
                            This diagnosis does NOT explain:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-5370">
                            <li data-id="element-5371">
                              • The other person's intentions, psychology, or
                              motivations
                            </li>
                            <li data-id="element-5372">
                              • Their personality, management style, or
                              character
                            </li>
                            <li data-id="element-5373">
                              • Who is correct or incorrect in specific
                              conflicts
                            </li>
                            <li data-id="element-5374">
                              • What they should do differently or how they
                              should change
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5375">
                  <div className="flex items-start gap-4 mb-4" data-id="element-5376">
                    <span className="text-2xl" data-id="element-5377">🧭</span>
                    <div className="flex-1" data-id="element-5378">
                      <h2 className="text-xl font-medium mb-3" data-id="element-5379">
                        Integration Summary
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5380">
                        This is your system responding to a specific role
                        interface. When this type of authority or power dynamic
                        is present, your pattern shifts in predictable ways:
                        vigilance increases, containment tightens, or withdrawal
                        activates. The pattern repeats across similar role
                        configurations because it is a structural response to
                        power asymmetry, not a reaction to individual
                        personalities. The dynamic is not about who is right —
                        it is about how your system distorts under this
                        particular interface.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-5381">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-5382">
                    💾 Save Decode
                  </button>
                  <Link to="/report-detail?report=deep-work-decode" className="block w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors text-center" data-id="element-5383">
                    ➡️ Deep Work Decode
                  </Link>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}